import { useCallback, useEffect, useState } from "react";
import { Plus, Repeat, Prohibit } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ModalShell } from "../components/ModalShell";
import { AmountInput } from "../components/AmountInput";
import api, { fmtGNF, fmtDate, apiError, logError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const CATEGORIES = ["Loyer", "Salaires", "Électricité", "Eau", "Transport", "Achats/Marchandises", "Paiements fournisseurs", "Communication", "Autre"];

export default function Expenses() {
  const { can } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [recurring, setRecurring] = useState([]);
  const [show, setShow] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [form, setForm] = useState({ category: "", amount: "", description: "", day_of_month: "1" });

  const load = useCallback(() => {
    api.get("/expenses").then((r) => setExpenses(r.data)).catch((e) => logError(e));
    api.get("/recurring-expenses").then((r) => setRecurring(r.data)).catch((e) => logError(e));
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      if (show === "recurring") {
        await api.post("/recurring-expenses", { category: form.category, amount: parseFloat(form.amount), description: form.description, day_of_month: parseInt(form.day_of_month) || 1 });
        toast.success("Dépense récurrente configurée — enregistrée chaque mois automatiquement");
      } else {
        await api.post("/expenses", { category: form.category, amount: parseFloat(form.amount), description: form.description });
        toast.success("Dépense enregistrée");
      }
      setShow(null); setForm({ category: "", amount: "", description: "", day_of_month: "1" }); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const cancelExpense = async () => {
    try {
      await api.post(`/expenses/${cancelTarget.id}/cancel`, { reason: cancelReason });
      toast.success("Dépense annulée — retirée des totaux, trésorerie recalculée");
      setCancelTarget(null); setCancelReason(""); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4 fade-in" data-testid="expenses-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="font-display font-bold text-2xl">Dépenses</h1>
        <div className="flex gap-2">
          <button data-testid="add-recurring-btn" onClick={() => setShow("recurring")} className="btn-ghost py-2 px-4 flex items-center gap-1.5 text-sm"><Repeat size={16} /> Récurrente</button>
          <button data-testid="add-expense-btn" onClick={() => setShow("once")} className="btn-amber py-2 px-4 flex items-center gap-1.5 text-sm"><Plus size={16} weight="bold" /> Dépense</button>
        </div>
      </div>

      {recurring.length > 0 && (
        <div className="kcard p-5">
          <h3 className="font-display font-bold mb-3">Dépenses récurrentes</h3>
          <div className="space-y-2">
            {recurring.map((r) => (
              <div key={r.id} data-testid="recurring-row" className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-[#F6F4EF]/60">
                <Repeat size={16} className="text-gray-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold">{r.category} — {fmtGNF(r.amount)}</p>
                  <p className="text-xs text-gray-500">Le {r.day_of_month} de chaque mois {r.active ? "" : "· désactivée"}</p>
                </div>
                <button data-testid="toggle-recurring-btn" onClick={async () => { await api.patch(`/recurring-expenses/${r.id}`); load(); }}
                  className={`text-xs font-semibold ${r.active ? "text-[#B4432F]" : "text-[#2E7D5B]"}`}>
                  {r.active ? "Désactiver" : "Réactiver"}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-2">
        {expenses.map((e) => (
          <div key={e.id} data-testid="expense-row" className={`kcard p-4 flex items-center gap-3 ${e.cancelled ? "opacity-70" : ""}`}>
            <div className="flex-1 min-w-0">
              <p className={`font-semibold text-sm ${e.cancelled ? "line-through" : ""}`}>{e.category} {e.auto && <span className="text-xs font-normal text-gray-400">(auto)</span>} {e.type === "recurrente" && <Repeat size={13} className="inline text-gray-400" />}</p>
              <p className="text-xs text-gray-500 truncate">{e.description || "—"} · {e.author_name} · {fmtDate(e.created_at)}</p>
              {e.cancelled && (
                <p data-testid={`expense-cancelled-note-${e.id}`} className="text-xs text-[#B4432F] font-semibold mt-0.5">
                  Annulée par {e.cancelled_by} le {fmtDate(e.cancelled_at)} — {e.cancel_reason}
                </p>
              )}
            </div>
            {!e.cancelled && !e.auto && can("expenses_cancel") && (
              <button data-testid={`cancel-expense-btn-${e.id}`} onClick={() => { setCancelTarget(e); setCancelReason(""); }}
                className="text-xs text-[#B4432F] font-semibold flex items-center gap-1 shrink-0"><Prohibit size={13} /> Annuler</button>
            )}
            <span className={`font-display font-bold tabular-nums text-[#B4432F] ${e.cancelled ? "line-through" : ""}`}>-{fmtGNF(e.amount)}</span>
          </div>
        ))}
        {expenses.length === 0 && <div className="kcard p-8 text-center text-sm text-gray-400">Aucune dépense enregistrée.</div>}
      </div>

      {cancelTarget && (
        <ModalShell size="max-w-sm" onClose={() => setCancelTarget(null)}>
          <h3 className="font-display font-bold text-lg">Annuler cette dépense</h3>
          <p className="text-sm text-gray-600">
            {cancelTarget.category} — {fmtGNF(cancelTarget.amount)}. La dépense reste visible dans l'historique,
            barrée, mais n'est plus comptée dans les totaux ni la trésorerie. L'annulation n'est pas réversible.
          </p>
          <input data-testid="cancel-expense-reason-input" className="kinput" placeholder="Motif de l'annulation (obligatoire)"
            value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
          <div className="flex gap-2">
            <button className="btn-ghost flex-1 py-2.5" onClick={() => setCancelTarget(null)}>Revenir</button>
            <button data-testid="cancel-expense-confirm-btn" className="btn-amber flex-1 py-2.5"
              disabled={cancelReason.trim().length < 5} onClick={cancelExpense}>Confirmer l'annulation</button>
          </div>
        </ModalShell>
      )}

      {show && (
        <ModalShell size="max-w-sm" onClose={() => setShow(null)}>
            <h3 className="font-display font-bold text-lg">{show === "recurring" ? "Dépense récurrente" : "Nouvelle dépense"}</h3>
            <select data-testid="expense-category-select" className="kinput" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
              <option value="">Catégorie (obligatoire)</option>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <AmountInput data-testid="expense-amount-input" placeholder="Montant (GNF)" value={form.amount}
              onChange={(v) => setForm({ ...form, amount: v })} onOverflow={(m) => toast.error(m)} />
            <input data-testid="expense-description-input" className="kinput" placeholder="Description (justificatif texte)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            {show === "recurring" && (
              <input data-testid="expense-day-input" type="number" min="1" max="28" className="kinput" placeholder="Jour du mois (1-28)" value={form.day_of_month} onChange={(e) => setForm({ ...form, day_of_month: e.target.value })} />
            )}
            <div className="flex gap-2">
              <button className="btn-ghost flex-1 py-2.5" onClick={() => setShow(null)}>Annuler</button>
              <button data-testid="expense-create-btn" className="btn-amber flex-1 py-2.5" disabled={!form.category || !form.amount} onClick={create}>Enregistrer</button>
            </div>
          </ModalShell>
      )}
    </div>
  );
}
